import React from 'react';
import { Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { formatCurrency } from '../../utils/currency';
import { useBaseCurrency } from '../../stores/currency-store';
import type { DashboardLink } from '../../services/dashboard-service';

/**
 * Pieces shared by the home dashboard's sections (issue #27).
 *
 * Sections never navigate themselves: they hand a DashboardLink to
 * `onNavigate`, which the page maps onto the router. That keeps every section
 * renderable in a test without a router, and keeps the backend the one place
 * that decides where a row leads.
 */
export type NavigateFn = (link: DashboardLink) => void;

/** Formats a base-currency amount. Dashboard totals are always in base. */
export function useMoney() {
  const base = useBaseCurrency();
  return (amount: string | number | null | undefined, opts: { compact?: boolean } = {}) =>
    formatCurrency(amount, base, { base, compact: opts.compact });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Oct 6" from "2026-10-06", parsed as a calendar date. new Date("2026-10-06")
 * is UTC midnight, which renders as Oct 5 anywhere west of Greenwich.
 */
export function shortDate(ymd: string | null | undefined, now: Date = new Date()): string {
  if (!ymd) return '';
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!m || !d) return ymd;
  // The year only when it is not this one: "Sep 24" for 2024 reads as last month.
  return y === now.getFullYear() ? `${MONTHS[m - 1]} ${d}` : `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "October 2026" from "2026-10" or "2026-10-01". */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
  return m ? `${names[m - 1]} ${y}` : ym;
}

/** "today", "yesterday", "3 days ago" for a timestamp. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const t = new Date(iso);
  const mins = Math.round((now.getTime() - t.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export const num = (s: string | null | undefined) => {
  const n = parseFloat(s ?? '0');
  return Number.isFinite(n) ? n : 0;
};

interface SectionCardProps {
  title: string;
  subtitle?: React.ReactNode;
  action?: { label: string; link: DashboardLink };
  onNavigate?: NavigateFn;
  children: React.ReactNode;
  /** Region label for assistive tech; defaults to the title. */
  ariaLabel?: string;
}

/** The card every section sits in: title, optional subtitle and one action. */
export const SectionCard: React.FC<SectionCardProps> = ({
  title, subtitle, action, onNavigate, children, ariaLabel,
}) => (
  <Card component="section" aria-label={ariaLabel ?? title} sx={{ height: '100%' }}>
    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontSize: '1.05rem', fontWeight: 650 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && onNavigate && (
          <Button size="small" endIcon={<ArrowForward fontSize="small" />}
            onClick={() => onNavigate(action.link)} sx={{ flexShrink: 0, textTransform: 'none' }}>
            {action.label}
          </Button>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </CardContent>
  </Card>
);

/** A quiet one-line empty state inside a section. */
export const EmptyLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', py: 2 }}>
    {children}
  </Typography>
);

/** Placeholder while the summary loads, the same height as a section. */
export const SectionSkeleton: React.FC<{ title: string; rows?: number }> = ({ title, rows = 4 }) => (
  <SectionCard title={title}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="text" height={28} />
    ))}
  </SectionCard>
);

/** A labelled figure: "Paid  $9,874.33". */
export const Figure: React.FC<{ label: string; value: string; tone?: 'in' | 'out' | 'warn' | 'plain' }> = ({
  label, value, tone = 'plain',
}) => {
  const color = {
    in: 'var(--inflow-text)', out: 'var(--outflow-text)', warn: 'var(--warn-text)', plain: 'var(--text)',
  }[tone];
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>
        {label}
      </Typography>
      <Typography className="fw-num" sx={{ fontWeight: 700, fontSize: '1.05rem', color }}>
        {value}
      </Typography>
    </Box>
  );
};
